import { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Star } from 'lucide-react';
import useAuth from '../../hooks/global/useAuth';
import useBasket from '../../hooks/basket/useBasket';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import { COUPON_TYPES } from '../../services/exports/Constants';
import { StoreInterface } from '../../store/types';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';
import useActiveDiscount from '../../features/discount-exclusivity/useActiveDiscount';
import useDiscountDisplacement from '../../features/discount-exclusivity/useDiscountDisplacement';
import useAppMethods from '../../hooks/utility/useAppMethods';
import giftCardSlice, { selectGiftCard } from '../../store/slices/GiftCardSlice';

interface Props {
  className?: string;
}

export default function CashbackPointsRow(props: Props) {
  const { className } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:CashbackPointsRow' });

  const { t: tExclusivity } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.displacement' });
  const { isLoggedIn } = useAuth();
  const { hasCashbackProgram, balance, getRedemptionOptions, pointsToMoney } = useCashbackProgram();
  const { actualSubtotal } = useBasket();
  const { formatCurrency } = useAppMethods();
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const giftCard = useSelector(selectGiftCard);
  const activeDiscount = useActiveDiscount();
  const { recordPointsDisplacedCoupon } = useDiscountDisplacement();

  const options = useMemo<number[]>(() => getRedemptionOptions(actualSubtotal), [getRedemptionOptions, actualSubtotal]);

  const isCashbackActive = cached_order?.coupon_type === COUPON_TYPES.CASHBACK;
  const selectedPoints = cached_order?.cashback_points ?? null;

  // A promo/gift is active and would be removed by redeeming points (mutually exclusive) — the
  // pre-emptive hint (Layer A) names it before the customer taps. Null when nothing to warn about.
  const displaceableCoupon =
    activeDiscount?.kind === 'promo' || activeDiscount?.kind === 'gift' ? activeDiscount : null;

  // Stale selections are reconciled app-wide by useReconcileCashbackPoints (OrderFlowProvider).

  if (!hasCashbackProgram || !isLoggedIn || options.length === 0) {
    return null;
  }

  const selectOption = (value: number): void => {
    if (isCashbackActive && selectedPoints === value) {
      return;
    }

    if (!isCashbackActive) {
      toast.success(t('toasts.enabled'));
    }

    // Redeeming points is exclusive with a promo/gift — record what it displaces (Layer B) so the
    // notice can explain it + offer Undo. For a gift we must also clear the gift slice (a promo is
    // cleared implicitly by overwriting coupon_type below); records at the "use points" gesture.
    if (!isCashbackActive && displaceableCoupon) {
      const couponKind = displaceableCoupon.kind as 'promo' | 'gift';
      recordPointsDisplacedCoupon({
        kind: couponKind,
        label: displaceableCoupon.label,
        couponSaving: displaceableCoupon.saving != null ? String(formatCurrency(displaceableCoupon.saving)) : null,
        points: value,
        pointsSaving: String(formatCurrency(pointsToMoney(value))),
        giftCard: couponKind === 'gift' ? (giftCard ?? undefined) : undefined,
      });

      if (couponKind === 'gift') {
        ReduxActions.dispatch(giftCardSlice.actions.apply(undefined));
      }
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.CASHBACK,
        coupon_details: null,
        cashback_points: value,
      }),
    );
  };

  const disableCashback = () => {
    if (!isCashbackActive) {
      return;
    }

    toast.success(t('toasts.removed'));

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: null,
        coupon_details: null,
        cashback_points: null,
      }),
    );
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-sm">
        <p className="text-sm font-bold text-brand-text-primary flex items-center gap-2xs">
          <Star className="w-4 h-4 fill-brand-primary text-brand-primary" />
          {t('header.title')}
        </p>
        <span className="text-[11px] text-muted-foreground">{t('header.balance', { value: balance })}</span>
      </div>
      {/* Layer A — zero-friction hint (UX feedback item 3): only while a promo/gift is active, tell
          the customer that redeeming points will remove it, before they tap anything. */}
      {displaceableCoupon && (
        <p className="text-[11px] text-muted-foreground -mt-2xs mb-sm" data-test="points-will-remove-hint">
          {displaceableCoupon.saving != null
            ? tExclusivity(`points_hint.${displaceableCoupon.kind}`, {
                name: displaceableCoupon.label,
                saving: formatCurrency(displaceableCoupon.saving),
              })
            : tExclusivity(`points_hint.${displaceableCoupon.kind}_no_saving`, { name: displaceableCoupon.label })}
        </p>
      )}
      <div className="flex gap-xs">
        {options.map((value) => (
          <button
            key={`cashback-option-${value}`}
            type="button"
            data-test={`cashback-option-${value}`}
            onClick={() => selectOption(value)}
            className={classnames(pillBase, isCashbackActive && selectedPoints === value ? pillActive : pillInactive)}
          >
            {t('option_label', { value })}
          </button>
        ))}
        <button
          type="button"
          data-test="cashback-option-none"
          onClick={disableCashback}
          className={classnames(pillBase, !isCashbackActive ? pillActive : pillInactive)}
        >
          {t('buttons.none')}
        </button>
      </div>
    </div>
  );
}

const pillBase = 'flex-1 py-xs rounded-full text-xs font-semibold transition-colors';

const pillActive = 'bg-brand-primary text-brand-button-primary-text';

const pillInactive = 'bg-muted text-muted-foreground hover:text-brand-text-primary';
